import { NodeConstructor } from "../Node";
import * as _ from "lodash";
import { PNumber } from "../properties/PNumber";
import { PBoolean } from "../properties/PBoolean";
import { PString } from "../properties/PString";
import { PColor } from "../properties/PColor";
import { PVector2 } from "../properties/PVector2";
import { PVector3 } from "../properties/PVector3";
import { PEuler } from "../properties/PEuler";
import { Color, Euler, Vector2, Vector3 } from "three";
import * as THREE from "three";

export interface ThreeMixinInterface<TThree> {
  init(): this;

  updateScene();

  toThree(threeValue: TThree);

  fromThree(threeValue: TThree): this;

  getGenericPropertiesFromThreejs(threeValue: TThree, mapper: (key, value) => any);

  onShow();
}

export function ThreeMixin<TNode extends NodeConstructor, TThree>(NodeClass: TNode) {
  abstract class ThreeMixin extends NodeClass implements ThreeMixinInterface<TNode> {
    threeValue: TThree;

    init(): this {
      super.init();
      this.toThree(this.threeValue);
      return this;
    }

    /**
     * aktualisiert die 3D-Szene
     */
    updateScene() {}

    /**
     * wird beim Anzeigen im Editor aufgerufen
     */
    onShow() {}

    toThree(threeValue) {
      const props = this.getProperties().filter((p) => p.isThree !== undefined && p.isThree(p));
      props.forEach((p) => p.toThree(threeValue));
    }

    fromThree(threeValue): this {
      const typeProp = this.findProperty("Type");
      if (typeProp) {
        const typeName = typeProp["selectOptions"].find((t) => threeValue.constructor == THREE[t]);
        typeProp.set(typeName);
        this.threeValue = threeValue.clone();
      }
      const props = this.getProperties().filter((p) => p.isThree !== undefined && p.isThree(p));
      props.forEach((p) => p.fromThree(threeValue));
      return this;
    }

    getGenericPropertiesFromThreejs(threeValue, mapper = (key, value) => undefined) {
      if (threeValue == null) {
        return [];
      }
      let keys = Object.keys(threeValue).filter(
        (key) =>
          !/^(id|defines|userData|uuid|type|parent|children|up|frustumCulled|matrix.*|renderOrder.*)$/g.test(
            key,
          ) &&
          // private Felder nicht, ausser es gibt eine gleichnamige get-Methode:
          (!/^_.*$/g.test(key) || threeValue[key.substring(1)] !== undefined) &&
          !(/^is.*$/g.test(key) && THREE[key.substring(2)] != null),
      );
      return keys
        .map((key) => {
          const value = threeValue[key];
          let p = mapper(key, value);
          if (p !== undefined) {
            return p;
          }
          if (/^(_.*)$/g.test(key)) {
            p = {
              name: key.substring(1),
              nameThree: key,
            };
          } else {
            p = {
              name: key,
            };
          }
          p.node = this;
          if (_.isNumber(value)) {
            return new PNumber(p);
          }
          if (_.isBoolean(value)) {
            return new PBoolean(p);
          }
          if (_.isString(value)) {
            return new PString(p);
          }
          if (_.isArray(value)) {
            return new PString(p);
          }
          if (value instanceof Color) {
            return new PColor(p);
          }
          if (value instanceof Vector2) {
            return new PVector2(p);
          }
          if (value instanceof Vector3) {
            return new PVector3(p);
          } else if (value instanceof Euler) {
            return new PEuler(p);
          } else {
            console.warn("unknown type", value, p);
          }
          return null;
        })
        .filter((p) => p != null);
    }
  }

  return ThreeMixin;
}
